/** Forgot password, change password, and "set a new password" after a mail link (ADR 0026). */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../components/Dialog';
import {
  changePassword,
  MIN_PASSWORD,
  PasswordProblem,
  requestPasswordReset,
  setNewPassword,
  setPasswordFromMail,
  openedFromInvite,
  type MailLink,
  usePasswordRecovery,
} from './password';
import { useSyncState } from './useSync';

const field = 'w-full rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';
const primary =
  'rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg disabled:opacity-50';

function useRun() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setError(
        t(`password.errors.${e instanceof PasswordProblem ? e.code : 'failed'}`, {
          min: MIN_PASSWORD,
        }),
      );
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

/** Ask for a reset mail. `onBack` returns to the sign-in form. */
export function ForgotPassword({
  initialEmail = '',
  onBack,
}: {
  initialEmail?: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail);
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useRun();
  return (
    <form
      className="flex flex-col gap-2 text-sm"
      aria-label={t('password.forgotTitle')}
      onSubmit={(e) => {
        e.preventDefault();
        void run(() => requestPasswordReset(email)).then((ok) => ok && setSent(true));
      }}
    >
      <p className="text-xs text-fg-muted">{t('password.forgotIntro')}</p>
      <label className="flex flex-col gap-1">
        {t('sync.email')}
        <input
          type="email"
          required
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={field}
        />
      </label>
      {sent && (
        <p role="status" className="text-xs text-fg-muted">
          {t('password.sent')}
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={busy || !email.trim()} className={primary}>
          {t('password.send')}
        </button>
        <button type="button" onClick={onBack} className="rounded-md px-3 py-1.5 hover:bg-bg-hover">
          {t('password.back')}
        </button>
      </div>
    </form>
  );
}

/** Signed in: change the password (the current one is checked first). */
export function ChangePassword() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);
  const { busy, error, run } = useRun();

  if (!open)
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setDone(false);
          }}
          className="self-start rounded-lg border border-border px-2 py-1 text-sm hover:bg-bg-hover"
        >
          {t('password.change')}
        </button>
        {done && (
          <span role="status" className="text-xs text-fg-muted">
            {t('password.changed')}
          </span>
        )}
      </div>
    );
  return (
    <form
      className="flex flex-col gap-2 rounded-lg border border-border p-3 text-sm"
      aria-label={t('password.change')}
      onSubmit={(e) => {
        e.preventDefault();
        void run(() => changePassword(current, next, repeat)).then((ok) => {
          if (!ok) return;
          setCurrent('');
          setNext('');
          setRepeat('');
          setOpen(false);
          setDone(true);
        });
      }}
    >
      <label className="flex flex-col gap-1">
        {t('password.current')}
        <input
          type="password"
          required
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className={field}
        />
      </label>
      <NewPasswordFields next={next} repeat={repeat} setNext={setNext} setRepeat={setRepeat} />
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className={primary}>
          {t('password.save')}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md px-3 py-1.5 hover:bg-bg-hover"
        >
          {t('password.cancel')}
        </button>
      </div>
    </form>
  );
}

function NewPasswordFields(p: {
  next: string;
  repeat: string;
  setNext: (v: string) => void;
  setRepeat: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <label className="flex flex-col gap-1">
        {t('password.new', { min: MIN_PASSWORD })}
        <input
          type="password"
          required
          minLength={MIN_PASSWORD}
          autoComplete="new-password"
          value={p.next}
          onChange={(e) => p.setNext(e.target.value)}
          className={field}
        />
      </label>
      <label className="flex flex-col gap-1">
        {t('password.repeat')}
        <input
          type="password"
          required
          autoComplete="new-password"
          value={p.repeat}
          onChange={(e) => p.setRepeat(e.target.value)}
          className={field}
        />
      </label>
    </>
  );
}

/**
 * Opened from a mail link that carries only its code: choose the password here.
 * The code is used on save (`onDone`), so a mail scanner opening the link can't use it up.
 */
export function MailLinkPassword({
  link,
  onDone,
  onExpired,
}: {
  link: MailLink;
  onDone: () => void;
  onExpired: () => void;
}) {
  const { t } = useTranslation();
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [expired, setExpired] = useState(false);
  const { busy, error, run } = useRun();
  const invite = link.type === 'invite';
  return (
    <form
      aria-labelledby="mail-link-title"
      className="flex flex-col gap-2 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          try {
            await setPasswordFromMail(link, next, repeat);
          } catch (err) {
            if (err instanceof PasswordProblem && err.code === 'expired') setExpired(true);
            throw err;
          }
        }).then((ok) => ok && onDone());
      }}
    >
      <h1 id="mail-link-title" className="text-lg font-semibold">
        {t(invite ? 'password.inviteTitle' : 'password.newTitle')}
      </h1>
      <p className="text-xs text-fg-muted">
        {t(invite ? 'password.inviteIntro' : 'password.newIntro')}
      </p>
      <NewPasswordFields next={next} repeat={repeat} setNext={setNext} setRepeat={setRepeat} />
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        {expired ? (
          <button
            type="button"
            onClick={onExpired}
            className="text-xs text-fg-muted underline hover:text-fg"
          >
            {t('password.forgot')}
          </button>
        ) : (
          <span />
        )}
        <button type="submit" disabled={busy} className={primary}>
          {t('password.save')}
        </button>
      </div>
    </form>
  );
}

/** Opened from a reset mail and signed in by its link: choose the new password. */
export function NewPasswordDialog() {
  const { t } = useTranslation();
  const pending = usePasswordRecovery((s) => s.pending);
  const signedIn = useSyncState((s) => !!s.session);
  const done = usePasswordRecovery((s) => s.done);
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const { busy, error, run } = useRun();
  return (
    <Dialog
      open={pending && signedIn}
      onOpenChange={(o) => !o && done()}
      title={t(openedFromInvite ? 'password.inviteTitle' : 'password.newTitle')}
    >
      <form
        className="flex flex-col gap-2 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          void run(() => setNewPassword(next, repeat)).then((ok) => ok && done());
        }}
      >
        <p className="text-xs text-fg-muted">
          {t(openedFromInvite ? 'password.inviteIntro' : 'password.newIntro')}
        </p>
        <NewPasswordFields next={next} repeat={repeat} setNext={setNext} setRepeat={setRepeat} />
        {error && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className={primary}>
            {t('password.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
